import {
  Avatar,
  Badge,
  Box,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  StarIcon,
} from "@phosphor-icons/react"
import { matchesName } from "@dehub/theme"
import dayjs from "dayjs"
import { useMemo, useState } from "react"

export type StaffStatus =
  | "AVAILABLE"
  | "ON_DUTY"
  | "OFF_DUTY"
  | "SUSPENDED"
  | null

export interface StaffOption {
  id: string
  name: string
  initials: string
  phone?: string | null
  status?: StaffStatus
  licenseExpiresAt?: string | null
  preferred?: boolean
}

interface Props {
  title: string
  options: StaffOption[]
  selectedId: string | null
  onChange: (id: string) => void
  searchPlaceholder?: string
  emptyHint?: string
  loading?: boolean
  showLicenseBadge?: boolean
}

function statusBadge(status: StaffStatus) {
  if (status === "AVAILABLE")
    return <Badge size="xs" color="green" variant="light">Бэлэн</Badge>
  if (status === "ON_DUTY")
    return <Badge size="xs" color="yellow" variant="light">Ажилтай</Badge>
  if (status === "OFF_DUTY")
    return <Badge size="xs" color="gray" variant="light">Завгүй</Badge>
  if (status === "SUSPENDED")
    return <Badge size="xs" color="red" variant="light">Зогссон</Badge>
  return null
}

function licenseBadge(licenseExpiresAt: string | null | undefined) {
  if (!licenseExpiresAt) return null
  const valid = dayjs(licenseExpiresAt).isAfter(dayjs())
  return (
    <Badge size="xs" color={valid ? "blue" : "red"} variant="light">
      {valid ? "Зөвш." : "Хугацаа дууссан"}
    </Badge>
  )
}

export function StaffPickerList({
  title,
  options,
  selectedId,
  onChange,
  searchPlaceholder = "Хайх...",
  emptyHint = "Олдсонгүй",
  loading,
  showLicenseBadge,
}: Props) {
  const [search, setSearch] = useState("")
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    // The visible name may be Latin while the stored name is Cyrillic (or the
    // other way round); matchesName folds both sides. Phone stays a plain match.
    return options.filter(
      (o) =>
        matchesName(o.name, search) ||
        (o.phone ?? "").toLowerCase().includes(q),
    )
  }, [options, search])

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={700}>
          {title}{" "}
          <Text component="span" size="sm" c="dimmed" fw={500}>
            ({options.length})
          </Text>
        </Text>
      </Group>
      <TextInput
        size="sm"
        placeholder={searchPlaceholder}
        leftSection={<MagnifyingGlassIcon size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <ScrollArea h={180} type="auto">
        <Stack gap={4}>
          {loading && <Loader size="xs" />}
          {!loading && filtered.length === 0 && (
            <Text size="sm" c="dimmed">
              {emptyHint}
            </Text>
          )}
          {filtered.map((opt) => {
            const isSelected = opt.id === selectedId
            return (
              <Box
                key={opt.id}
                onClick={() => onChange(opt.id)}
                p="xs"
                style={{
                  cursor: "pointer",
                  borderRadius: "var(--mantine-radius-sm)",
                  background: isSelected
                    ? "var(--mantine-color-brand-0)"
                    : "transparent",
                  borderLeft: isSelected
                    ? "3px solid var(--mantine-color-brand-6)"
                    : "3px solid transparent",
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <Avatar size={28} radius="xl" color="brand">
                    {opt.initials}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" fw={600} lineClamp={1}>
                        {opt.name}
                      </Text>
                      {opt.preferred && (
                        <StarIcon size={12} weight="fill" color="var(--mantine-color-yellow-6)" />
                      )}
                    </Group>
                    {opt.phone && (
                      <Group gap={3} wrap="nowrap">
                        <PhoneIcon size={11} color="var(--mantine-color-gray-6)" />
                        <Text size="sm" c="dimmed">
                          {opt.phone}
                        </Text>
                      </Group>
                    )}
                  </Box>
                  <Group gap={4} wrap="nowrap">
                    {statusBadge(opt.status ?? null)}
                    {showLicenseBadge && licenseBadge(opt.licenseExpiresAt)}
                    {isSelected && (
                      <CheckCircleIcon size={16} weight="fill" color="var(--mantine-color-brand-6)" />
                    )}
                  </Group>
                </Group>
              </Box>
            )
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}
